import React, { useEffect, useMemo, useCallback } from "react";
import { Box, Skeleton, Tooltip } from "@mui/material";
import {
  ExpandMoreRounded,
  HubRounded,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getMachines } from "../../machinery/customerMachinerySlice";
import { setMachineContext, setMachineContextDirect, setMessages, setNewThreadPending, startNewChat } from "../../../shared/chatBot/chatBotSlice";
import {
  toggleMachineExpand,
  fetchThreadCounts,
  fetchMachineThreads,
  loadThread,
  renameThread,
  deleteThread,
  setActiveThreadId,
} from "../midasPageSlice";
import { Machine } from "../../machinery/models/models";
import { dt } from "../../../shared/themes/redesignTokens";
import { SidebarState } from "../midasPageModels";
import MachineThreadAccordion from "./MachineThreadAccordion";

// ===================================================================
//  MIDAS Design Tokens (local)
// ===================================================================

const midas = {
  gold: "#D4A843",
  goldSoft: "rgba(212, 168, 67, 0.06)",
  goldBorder: "rgba(212, 168, 67, 0.3)",
  goldPillBg: "rgba(212, 168, 67, 0.12)",
  statusGreen: "#27AE60",
  statusAmber: "#F39C12",
  statusRed: "#E74C3C",
  neutralPillBg: "#F0F0F0",
  neutralPillText: "#6B7280",
  platformCardBg: "#F8F9FB",
  dividerLine: "#E5E7EB",
  dividerText: "#9CA3AF",
  inactiveBorderLeft: "#E5E7EB",
};

const MAX_VISIBLE = 20;

// ===================================================================
//  Status dot color logic
// ===================================================================

const getStatusColor = (machine: Machine): string => {
  const count = machine.activeServiceRequestCount || 0;
  const statusColor = machine.serviceRequestStatusColor || "";
  if (count === 0) return midas.statusGreen;
  if (statusColor === "red" || statusColor === "error") return midas.statusRed;
  return midas.statusAmber;
};

// ===================================================================
//  Thread Count Pill — shows count badge on right edge of card
// ===================================================================

const ThreadCountPill: React.FC<{
  count: number;
  isExpanded: boolean;
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
}> = ({ count, isExpanded, isActive, onClick }) => {
  if (count === 0) return null;
  const pillBg = isActive ? midas.goldPillBg : midas.neutralPillBg;
  const pillBorder = isActive ? `1px solid ${midas.goldBorder}` : "none";
  const pillColor = isActive ? midas.gold : midas.neutralPillText;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "2px",
        padding: "2px 8px",
        borderRadius: "10px",
        border: pillBorder,
        backgroundColor: pillBg,
        cursor: "pointer",
        flexShrink: 0,
        transition: "all 0.15s ease",
        "&:hover": { opacity: 0.85 },
      }}
    >
      <Box
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "10px",
          fontWeight: 600,
          color: pillColor,
          lineHeight: 1,
        }}
      >
        {count}
      </Box>
      <ExpandMoreRounded
        sx={{
          fontSize: "12px",
          color: pillColor,
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 200ms ease",
        }}
      />
    </Box>
  );
};

// ===================================================================
//  MidasFleetChips
// ===================================================================

interface MidasFleetChipsProps {
  sidebarState: SidebarState;
  onMachineClick: (machine: Machine) => void;
  prefersReducedMotion?: boolean;
}

const MidasFleetChips: React.FC<MidasFleetChipsProps> = ({
  sidebarState,
  onMachineClick,
  prefersReducedMotion = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector((s) => s.customerMachinery.loading);
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const threadCounts = useAppSelector((s) => s.midasPage.threadCounts);
  const platformThreadCount = useAppSelector((s) => s.midasPage.platformThreadCount);
  const expandedMachines = useAppSelector((s) => s.midasPage.expandedMachines);
  const machineThreads = useAppSelector((s) => s.midasPage.machineThreads);
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);

  const isExpanded = sidebarState === "expanded";
  const isPlatformSelected = !machineContext;

  // Flatten machines
  const allMachines = useMemo(() => {
    const flat: Machine[] = [];
    if (machinesMap && typeof machinesMap === "object") {
      Object.values(machinesMap).forEach((group) => {
        if (Array.isArray(group)) flat.push(...group);
      });
    }
    return flat;
  }, [machinesMap]);

  // Fetch machines if not loaded
  useEffect(() => {
    if (allMachines.length === 0 && !machineryLoading) {
      dispatch(getMachines({}));
    }
  }, [allMachines.length, machineryLoading, dispatch]);

  // Fetch thread counts when machines are loaded
  useEffect(() => {
    if (allMachines.length > 0) {
      const ids = allMachines.slice(0, MAX_VISIBLE).map((m) => m.id);
      dispatch(fetchThreadCounts(ids));
    }
  }, [allMachines, dispatch]);

  const visibleMachines = allMachines.slice(0, MAX_VISIBLE);
  const totalCount = allMachines.length;

  // ── Card body click: clear chat + set context (lazy creation) ──
  // BUG FIX 2026-03-20: Don't use startNewChat() followed by async setMachineContext().
  // startNewChat clears machineContext immediately (.pending), but setMachineContext only
  // restores it after an API call (.fulfilled). During that gap, MidasWelcome renders the
  // platform greeting instead of the machine greeting. Fix: set machineContext synchronously
  // via setMachineContextDirect BEFORE clearing messages, so the welcome screen immediately
  // knows we're in machine mode.
  const handleCardBodyClick = useCallback(
    (machine: Machine) => {
      // 1. Clear messages and reset chat state
      dispatch(setMessages([]));
      dispatch(setActiveThreadId(null));
      dispatch(setNewThreadPending());
      // 2. Set machine context synchronously — MidasWelcome will see this immediately
      //    and render MidasMachineWelcome instead of the platform greeting.
      onMachineClick(machine);
    },
    [dispatch, onMachineClick]
  );

  const handlePlatformBodyClick = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    if (!isPlatformSelected) {
      dispatch(setMachineContext(null));
    }
  }, [dispatch, isPlatformSelected]);

  // ── Pill click: toggle accordion ──
  const handlePillClick = useCallback(
    (e: React.MouseEvent, key: string, machineId: string | null) => {
      e.stopPropagation();
      dispatch(toggleMachineExpand(key));
      // Always refetch on expand — optimistic/createThread inserts plant incomplete
      // cache (MIDAS-BUG-004), so a `!machineThreads[key]` guard would skip the real
      // list fetch and leave the accordion showing 1-2 stale optimistic threads.
      if (!expandedMachines[key]) {
        dispatch(fetchMachineThreads(machineId));
      }
    },
    [dispatch, expandedMachines]
  );

  // ── Thread handlers ──
  const handleThreadClick = useCallback(
    (threadId: string) => {
      if (threadId === activeThreadId) return;
      dispatch(loadThread(threadId));
    },
    [dispatch, activeThreadId]
  );

  const handleNewChatForMachine = useCallback(
    (machine: Machine) => {
      // Same fix as handleCardBodyClick — clear messages + set context synchronously
      dispatch(setMessages([]));
      dispatch(setActiveThreadId(null));
      dispatch(setNewThreadPending());
      onMachineClick(machine);
    },
    [dispatch, onMachineClick]
  );

  const handleNewPlatformChat = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    dispatch(setMachineContext(null));
  }, [dispatch]);

  const handleRename = useCallback(
    (threadId: string, title: string) => {
      dispatch(renameThread({ id: threadId, title }));
    },
    [dispatch]
  );

  const handleDelete = useCallback(
    (threadId: string) => {
      dispatch(deleteThread(threadId));
    },
    [dispatch]
  );

  // ── Loading skeleton ──
  if (machineryLoading && allMachines.length === 0) {
    return (
      <Box sx={{ padding: isExpanded ? "12px 12px 0" : "8px 4px 0" }}>
        <Skeleton variant="rounded" width="100%" height={isExpanded ? 44 : 36} sx={{ mb: "8px", borderRadius: "8px" }} />
        {isExpanded && (
          <Box sx={{ fontFamily: dt.font, fontSize: "10px", fontWeight: 700, color: dt.textMuted, letterSpacing: "1.2px", textTransform: "uppercase", padding: "0 4px 8px" }}>
            {translate("midas.fleet.yourMachines")}
          </Box>
        )}
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" width="100%" height={isExpanded ? 36 : 32} sx={{ mb: "4px", borderRadius: "8px" }} />
        ))}
      </Box>
    );
  }

  // ═════════════════════════════════════════════════════════════
  //  Section label — shared styling for "CONVERSATIONS" / "YOUR MACHINES"
  // ═════════════════════════════════════════════════════════════

  const sectionLabelSx = {
    fontFamily: "'Sora', sans-serif",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "1.2px",
    color: dt.textMuted,
    textTransform: "uppercase" as const,
    whiteSpace: "nowrap" as const,
  };

  // ═════════════════════════════════════════════════════════════
  //  Platform card
  // ═════════════════════════════════════════════════════════════

  const platformExpKey = "platform";
  const isPlatformExpanded = !!expandedMachines[platformExpKey];

  const platformCard = isExpanded ? (
    <Box sx={{ backgroundColor: midas.platformCardBg, borderRadius: "10px", padding: "10px 12px" }}>
      <Box
        sx={{
          height: "44px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "0 8px 0 10px",
          borderRadius: isPlatformExpanded ? "8px 8px 0 0" : "8px",
          cursor: "pointer",
          position: "relative",
          backgroundColor: isPlatformSelected ? midas.goldSoft : "transparent",
          borderLeft: isPlatformSelected
            ? `3px solid ${midas.gold}`
            : "3px solid transparent",
          transition: "all 0.15s ease",
          "&:hover": {
            backgroundColor: isPlatformSelected ? midas.goldSoft : dt.rowHover,
          },
        }}
      >
        {/* Card body (~85%) */}
        <Box
          onClick={handlePlatformBodyClick}
          sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0, height: "100%" }}
        >
          <HubRounded
            sx={{
              fontSize: "18px",
              color: isPlatformSelected ? midas.gold : dt.textMuted,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: isPlatformSelected ? 600 : 500, color: isPlatformSelected ? dt.textPrimary : dt.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
              {translate("midas.threads.platform") || translate("midas.platform.title")}
            </Box>
            <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", fontWeight: 400, color: dt.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>
              {translate("midas.platform.subtitle")}
            </Box>
          </Box>
        </Box>

        {/* Right edge (~15%): pill + chevron — neutral gray for platform */}
        <ThreadCountPill
          count={platformThreadCount}
          isExpanded={isPlatformExpanded}
          isActive={false}
          onClick={(e) => handlePillClick(e, platformExpKey, null)}
        />
      </Box>

      {/* Platform thread accordion */}
      {isExpanded && (
        <Box sx={{ borderRadius: "0 0 8px 8px" }}>
          <MachineThreadAccordion
            machineId={null}
            isExpanded={isPlatformExpanded}
            threads={machineThreads[platformExpKey] || []}
            activeThreadId={activeThreadId}
            onThreadClick={handleThreadClick}
            onNewChat={handleNewPlatformChat}
            onRename={handleRename}
            onDelete={handleDelete}
            prefersReducedMotion={prefersReducedMotion}
          />
        </Box>
      )}
    </Box>
  ) : (
    <Tooltip title={translate("midas.platform.tooltip")} placement="right" arrow>
      <Box
        onClick={handlePlatformBodyClick}
        sx={{
          width: "40px",
          height: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "6px",
          cursor: "pointer",
          margin: "0 auto",
          backgroundColor: isPlatformSelected ? midas.goldSoft : "transparent",
          borderLeft: isPlatformSelected ? `2px solid ${midas.gold}` : "2px solid transparent",
          transition: "all 0.15s ease",
          "&:hover": { backgroundColor: isPlatformSelected ? midas.goldSoft : "rgba(0,0,0,0.04)" },
        }}
      >
        <HubRounded sx={{ fontSize: "18px", color: isPlatformSelected ? midas.gold : dt.textMuted }} />
      </Box>
    </Tooltip>
  );

  // ═════════════════════════════════════════════════════════════
  //  CONVERSATIONS label — shown above the platform card (expanded only)
  // ═════════════════════════════════════════════════════════════

  const conversationsLabel = isExpanded ? (
    <Box sx={{ ...sectionLabelSx, pl: "12px", mb: "8px" }}>
      {translate("midas.fleet.conversations")}
    </Box>
  ) : null;

  // ═════════════════════════════════════════════════════════════
  //  Empty state
  // ═════════════════════════════════════════════════════════════

  if (allMachines.length === 0) {
    return (
      <Box sx={{ padding: isExpanded ? "12px 12px 0" : "8px 4px 0", flexShrink: 0 }}>
        {conversationsLabel}
        {platformCard}
      </Box>
    );
  }

  // ═════════════════════════════════════════════════════════════
  //  Full fleet list
  // ═════════════════════════════════════════════════════════════

  return (
    <Box sx={{ padding: isExpanded ? "12px 12px 0" : "8px 4px 0", flexShrink: 0 }}>
      {conversationsLabel}
      {platformCard}

      {/* FLEET section label — "YOUR MACHINES (N)" with top divider */}
      {isExpanded && (
        <Box
          sx={{
            borderTop: `1px solid ${dt.cardBorder}`,
            mt: "12px",
            mb: "8px",
            pt: "12px",
            pl: "12px",
          }}
        >
          <Box sx={sectionLabelSx}>
            {translate("midas.fleet.yourMachines")} ({totalCount})
          </Box>
        </Box>
      )}

      {/* Machine cards.
          BUG-MIDAS-MOBILE-0414-B: Removed nested `overflowY: auto` here.
          The parent MidasSidebar already provides a `flex: 1, overflowY: auto`
          container (see MidasSidebar.tsx). Having a second scroll container
          inside it created a nested scroll surface that, on touch devices
          (Samsung Chrome), captured vertical touch gestures and prevented
          the user from scrolling the OUTER container up to reach the
          Platform card at the top. Single source of truth for scroll =
          MidasSidebar's inner wrapper. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        {visibleMachines.map((machine) => {
          const isActive = machineContext?.machineId === machine.id;
          const statusColor = getStatusColor(machine);
          const displayName = machine.model || "Unknown";
          const brandName = machine.customBrandName || machine.manufacturer?.name || "";
          const count = threadCounts[machine.id] || 0;
          const isMachineExpanded = !!expandedMachines[machine.id];

          if (!isExpanded) {
            // Collapsed: icon + status dot, click = clear + set context
            return (
              <Tooltip key={machine.id} title={`${brandName} ${displayName}`.trim()} placement="right" arrow>
                <Box
                  onClick={() => handleCardBodyClick(machine)}
                  sx={{
                    width: "40px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "6px",
                    cursor: "pointer",
                    position: "relative",
                    margin: "0 auto",
                    backgroundColor: isActive ? midas.goldSoft : "transparent",
                    borderLeft: isActive ? `2px solid ${midas.gold}` : "2px solid transparent",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: statusColor, boxShadow: `0 0 4px ${statusColor}` }} />
                </Box>
              </Tooltip>
            );
          }

          // Expanded: two-zone card + accordion
          // Left border: gold for active, gray for expanded-inactive, transparent otherwise
          const leftBorder = isActive
            ? `2px solid ${midas.gold}`
            : isMachineExpanded
              ? `2px solid ${midas.inactiveBorderLeft}`
              : "2px solid transparent";

          return (
            <Box key={machine.id}>
              <Box
                sx={{
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "0 8px 0 12px",
                  borderRadius: isMachineExpanded ? "8px 8px 0 0" : "8px",
                  position: "relative",
                  backgroundColor: isActive ? midas.goldSoft : "transparent",
                  borderLeft: leftBorder,
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: isActive ? midas.goldSoft : dt.rowHover },
                }}
              >
                {/* Card body (~85%): click = new chat */}
                <Box
                  onClick={() => handleCardBodyClick(machine)}
                  sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0, height: "100%", cursor: "pointer" }}
                >
                  <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: statusColor, boxShadow: `0 0 4px ${statusColor}`, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: isActive ? 600 : 500, color: isActive ? dt.textPrimary : dt.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
                      {brandName ? `${brandName} ${displayName}` : displayName}
                    </Box>
                    {machine.serialNumber && (
                      <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", fontWeight: 400, color: dt.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>
                        #{machine.serialNumber}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Right edge (~15%): pill + chevron — gold when active */}
                <ThreadCountPill
                  count={count}
                  isExpanded={isMachineExpanded}
                  isActive={isActive}
                  onClick={(e) => handlePillClick(e, machine.id, machine.id)}
                />
              </Box>

              {/* Thread accordion */}
              <Box sx={{ borderRadius: "0 0 8px 8px" }}>
                <MachineThreadAccordion
                  machineId={machine.id}
                  isExpanded={isMachineExpanded}
                  threads={machineThreads[machine.id] || []}
                  activeThreadId={activeThreadId}
                  onThreadClick={handleThreadClick}
                  onNewChat={() => handleNewChatForMachine(machine)}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MidasFleetChips;
